import type { AxiosError } from 'axios'
import axios, { isAxiosError } from 'axios'

import { toastErr } from '@/components/Toast/Toast'
import { track } from '@/utils/analytics'
import { isSilentRoleError } from '@/utils/api-error.util'
import { getApiUrl } from '@/utils/brand.util'
import { getLocale, t } from '@/utils/i18n.util'

import { authClient } from './auth.provider'

declare module 'axios' {
  export interface AxiosResponse<T = any> extends Promise<T> {
    data: T
  }
  export interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

export const req = axios.create({
  baseURL: getApiUrl(),
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

req.interceptors.request.use(
  async (req) => {
    const prodDomain = new URLSearchParams(window.location.search).get('prod-domain')
    if (prodDomain) req.headers.set('X-Prod-Domain', prodDomain)
    req.headers.set('X-User-Locale', getLocale() || 'en-US')
    return req
  },
  (err) => {
    return err
  },
)

const normalizeEndpoint = (url: string | undefined): string => {
  if (!url) return 'unknown'
  const path = url.split('?')[0]
  return path
    .replace(/\/[0-9]+(?=\/|$)/g, '/:id')
    .replace(/\/[0-9a-fA-F-]{16,}(?=\/|$)/g, '/:id')
    .slice(0, 100)
}

const API_ERROR_SAMPLE_RATE = 0.25

const API_ERROR_CODE_KEYS: Record<string, string> = {
  SUBSCRIPTION_ALREADY_PAUSED: 'error.subscription_already_paused',
}

const trackApiError = (err: AxiosError) => {
  if (Math.random() > API_ERROR_SAMPLE_RATE) return
  track('api_error', {
    endpoint: normalizeEndpoint(err.config?.url),
    status: err.response?.status,
    method: err.config?.method?.toUpperCase(),
  })
}

req.interceptors.response.use(
  (res) => {
    return res.data
  },
  async (err: Error | AxiosError) => {
    if (isAxiosError(err)) {
      trackApiError(err)
      // Before skipErrorToast: family.api.ts opts into rejection to read `code`, and
      // these two codes are still meaningful there.
      if (err.config?.skipErrorToast) return Promise.reject(err)
      if (isSilentRoleError(err.response?.data)) return null
      const errorMsg = err?.response?.data.error || err?.response?.data.message || err?.response?.data || err.message
      if (errorMsg === 'OLD_BACKEND_USER') {
        window.location.href = 'https://app.getmemowrite.com/'
        return
      }
      if (errorMsg === 'INVALID_EMAIL') {
        return { error: 'INVALID_EMAIL' }
      }
      if (errorMsg === 'Memowrite API error: Not Found') {
        await authClient.signOut()
      }
      if (typeof errorMsg === 'string' && API_ERROR_CODE_KEYS[errorMsg]) {
        toastErr(t(API_ERROR_CODE_KEYS[errorMsg]))
        return null
      }

      const code = err.response?.data?.code
      const toastId = code === 'INVALID_SESSION' ? 'invalid-session' : undefined
      toastErr(typeof errorMsg === 'string' ? errorMsg : t('error.generic_error'), toastId)
      return null
    } else {
      return err.message
    }
  },
)
